<template>
    <ModalBase
        :name="'editar-' + indumentaria.id"
        :title="'Editar la indumentaria ' + indumentaria.nombre"
        @close="close"
    >
        <div class="tw:space-y-6">
            <h4 class="tw:text-center tw:text-base tw:font-semibold tw:text-gray-700 tw:pb-2">
                Editar la indumentaria: {{ indumentaria.nombre }}
            </h4>

            <!-- Toggle: Marcar como recogida -->
            <ToggleSwitch v-model="localRecogida" variant="success">
                Marcar como recogida
            </ToggleSwitch>

            <!-- Observaciones -->
            <BaseInputText
                v-model="localObservaciones"
                name="observaciones"
                label="Observaciones recogida"
            />

            <!-- Tabla talla / cantidad / opciones -->
            <div v-if="puedeCambiar">
                <SimpleTable
                    :columns="tableColumns"
                    :data="tableData"
                    :flat="true"
                    :default-col-width="defaultColWidth"
                >
                    <template #cell-talla>
                        <span class="tw:text-gray-700">{{ currentTallaNombre }}</span>
                    </template>
                    <template #cell-cantidad>
                        <span class="tw:text-gray-700">{{ currentStock }}</span>
                    </template>
                    <template #cell-opciones>
                        <Button
                            @click="openCambiarTalla"
                            variant="primary"
                            size="sm"
                        >
                            Cambiar talla
                        </Button>
                    </template>
                </SimpleTable>

                <CambiarTalla
                    :indumentaria="indumentaria"
                    :lista-id="listaId"
                    :base-id="baseId"
                    @talla-cambiada="onTallaCambiada"
                    @close="closeCambiarTalla"
                />
            </div>
        </div>

        <template #footer>
            <div class="tw:flex tw:gap-2 tw:justify-end">
                <Button variant="ghost" @click="close">
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    :disabled="loading"
                    @click="guardar"
                >
                    Guardar
                </Button>
            </div>
        </template>
    </ModalBase>
</template>

<script setup>
    import { ref, computed, getCurrentInstance } from 'vue'
    import store from '@/store'
    import { config } from '@/config.js'
    import { useToast } from '@/composables/common/useToast'
    import ModalBase from '@/modules/admin/ui/ModalBase.vue'
    import Button from '@/modules/admin/ui/Button.vue'
    import ToggleSwitch from '@/modules/admin/ui/ToggleSwitch.vue'
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue'
    import SimpleTable from '@/modules/admin/ui/table/SimpleTable.vue'
    import CambiarTalla from '@/modules/admin/bases/components/modals/CambiarTalla.vue'

    const props = defineProps({
        indumentaria: {
            type: Object,
            required: true
        },
        listaId: {
            type: [String, Number],
            required: true
        },
        baseId: {
            type: [String, Number],
            required: true
        },
        defaultColWidth: {
            type: String,
            default: '140px'
        }
    })

    const emit = defineEmits(['close', 'actualizar'])
    const toast = useToast()
    const instance = getCurrentInstance()

    const loading = ref(false)
    const localRecogida = ref(Boolean(props.indumentaria.pivot?.recogida))
    const localObservaciones = ref(props.indumentaria.pivot?.observaciones || '')
    const selectedTalla = ref(null)
    const selectedStock = ref(null)

    const puedeCambiar = computed(() => {
        return !localRecogida.value
    })

    const currentTallaNombre = computed(() => {
        if (selectedTalla.value) return selectedTalla.value.nombre
        if (props.indumentaria.tallaSelect) return props.indumentaria.tallaSelect.nombre
        if (props.indumentaria.talla) return props.indumentaria.talla.nombre
        return ''
    })

    const currentStock = computed(() => {
        if (selectedStock.value) return selectedStock.value
        if (props.indumentaria.stockSelect) return props.indumentaria.stockSelect
        return props.indumentaria.pivot?.cantidad || '-'
    })

    const tableColumns = [
        { key: 'talla', label: 'Talla' },
        { key: 'cantidad', label: 'Cantidad' },
        { key: 'opciones', label: 'Opciones' },
    ]

    const tableData = computed(() => [{}])

    const close = () => {
        instance.proxy.$modal.hide('editar-' + props.indumentaria.id)
        emit('close')
    }

    const openCambiarTalla = () => {
        instance.proxy.$modal.show('cambiar-talla')
    }

    const closeCambiarTalla = () => {
        instance.proxy.$modal.hide('cambiar-talla')
    }

    const onTallaCambiada = ({ talla, stock }) => {
        selectedTalla.value = talla
        selectedStock.value = stock
    }

    const guardar = async() => {
        loading.value = true

        const indumentariaSelect = { ...props.indumentaria }

        if (selectedTalla.value) {
            indumentariaSelect.tallaSelect = selectedTalla.value
        }
        if (selectedStock.value) {
            indumentariaSelect.stockSelect = selectedStock.value
        }

        indumentariaSelect.pivot = {
            ...indumentariaSelect.pivot,
            recogida: localRecogida.value,
            observaciones: localObservaciones.value
        }

        const params = {
            filters: {},
            lista_id: props.listaId,
            base_id: props.baseId,
            indumentaria_id: props.indumentaria.indumentaria_id,
            indumentariaSelect: indumentariaSelect
        }

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.indumentaria_base.update'),
                params
            )
            toast.success(res.data.message || 'Indumentaria actualizada correctamente')
            emit('actualizar')
            close()
        } catch (error) {
            console.error('Error updating indumentaria:', error)
            toast.error(error.response?.data?.message || 'Error al actualizar la indumentaria')
        } finally {
            loading.value = false
        }
    }
</script>
